import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import AuthContext from "connections/auth/AuthProvider";

// ######### MATERIAL UI
import {
  makeStyles,
  Typography,
  Box,
  IconButton,
  Popover,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { toast } from "react-toastify";

// ######### SERVICES
import {
  downloadProcessedVariant,
  getTableData,
} from "views/Inputs/viewServices/apiCalls";
import { getScenarioPremises } from "views/Cockpit/viewServices/calls/apiCalls";
import { getSetupkeyMappingsBatch } from "views/Inputs/viewServices/apiRelationships";

// ######### STYLES
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import { useTranslation } from "react-i18next";
import {
  TABLE_FILTER_OPERATORS,
  TABLE_PAGE_SIZE_OPTIONS,
  TABLE_FILTER_PANEL_PROPS,
} from "components/Table/dataGridDefaults";
import GroupedColumnsManagement from "components/Table/GroupedColumnsManagement";

const useStyles = makeStyles((theme) => ({
  card: {
    flex: 1,
    minWidth: "400px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  rowCount: {
    fontSize: "0.8rem",
    color: grayColor[1],
    fontVariantNumeric: "tabular-nums",
  },
  gridWrap: { flex: 1, minHeight: 300, width: "100%" },
  grid: {
    border: "none",
    fontSize: "0.8rem",
    // greyed-out empty columns
    "& .rgm-empty-col": { backgroundColor: grayColor[13], color: grayColor[5] },
    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
      outline: "none",
    },
    // toolbar buttons in neutral black (the MUI default primary is a
    // vestigial template tint we avoid)
    "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
      color: "#2b2f33",
    },
  },
  errorMessage: {
    color: theme.palette.error.main,
    textAlign: "center",
    padding: theme.spacing(2),
  },
  noInputMessage: {
    fontStyle: "italic",
    color: grayColor[1],
    textAlign: "center",
    padding: theme.spacing(4),
  },
  keyInfoBtn: { padding: 4 },
  pop: { maxWidth: 360, padding: theme.spacing(1.5, 2) },
  popTitle: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#2b2f33",
    marginBottom: 2,
  },
  popDesc: { fontSize: "0.75rem", color: grayColor[1], marginBottom: 8 },
  popMeta: { fontSize: "0.72rem", color: grayColor[1], marginBottom: 8 },
  popMetaB: { color: grayColor[2], fontWeight: 600 },
  popPrem: {
    borderTop: `1px solid ${grayColor[11]}`,
    paddingTop: 6,
    marginTop: 6,
    fontSize: "0.75rem",
  },
  popPremLbl: { color: grayColor[1] },
  popPremVal: {
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    fontSize: "0.72rem",
    color: grayColor[2],
    wordBreak: "break-all",
  },
}));

// Toolbar: row count + quick search + column/filter controls + a single Export
// that triggers the backend download of the full processed variant.
function PreviewToolbar({
  countText,
  onExport,
  exportDisabled,
  isDownloading,
  exportLabel,
  countClass,
  searchLabel,
  spinnerColor,
}) {
  return (
    <GridToolbarContainer style={{ padding: "6px 8px", gap: 8 }}>
      <Typography className={countClass}>{countText}</Typography>
      <Box style={{ flex: 1 }} />
      <GridToolbarQuickFilter
        placeholder={searchLabel}
        debounceMs={400}
        variant="outlined"
        size="small"
      />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Tooltip title={exportLabel}>
        <span>
          <IconButton
            size="small"
            onClick={onExport}
            disabled={exportDisabled}
            aria-label={exportLabel}
            style={{ color: "#2b2f33" }}
          >
            {isDownloading ? (
              <CircularProgress size={16} style={{ color: spinnerColor }} />
            ) : (
              <GetAppIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </GridToolbarContainer>
  );
}
PreviewToolbar.propTypes = {
  countText: PropTypes.string,
  onExport: PropTypes.func,
  exportDisabled: PropTypes.bool,
  isDownloading: PropTypes.bool,
  exportLabel: PropTypes.string,
  countClass: PropTypes.string,
  searchLabel: PropTypes.string,
  spinnerColor: PropTypes.string,
};

const isEmptyVal = (v) => v === null || v === undefined || v === "";

// key composition -> value using this row's component columns (mirrors the
// backend "component or empty" join used when the check generates the key)
function composeKey(composition, row) {
  if (!Array.isArray(composition) || composition.length === 0) return "—";
  return composition
    .map((col) => {
      const v = row?.[col];
      return isEmptyVal(v) ? "None" : String(v);
    })
    .join("_");
}

const DEFAULT_PAGE_SIZE = 50;

export default function TabTablePreview({
  tableId,
  columnsMeta,
  selectedVariant,
  reloadToken,
  onLoadingChange,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const classes = useStyles();
  const T_PREFIX = "rgm.inputs.table_preview";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [isDownloading, setIsDownloading] = useState(false);
  const [premises, setPremises] = useState([]); // [{premise_key, premise_label}]
  const [mappingsByPremise, setMappingsByPremise] = useState({}); // {premise_key: {table: {col: [components]}}}
  const [keyPopover, setKeyPopover] = useState(null); // {anchor, columnId, columnName, row}

  // ######### SERVER-SIDE DATA (pagination / sort / filter)
  const [rawRows, setRawRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emptyCols, setEmptyCols] = useState([]); // computed over the whole variant
  const emptyColsVariantRef = useRef(null); // variant the emptyCols belong to

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  // filter is applied after a debounce so typing doesn't spam the backend
  const [activeFilter, setActiveFilter] = useState({ items: [] });

  const showing = rawRows.length;

  // Reset paging/sort/filter when the table or variant changes. Also clear the
  // previous variant's rows so the grid shows the skeleton loading overlay
  // during the switch instead of the stale (greyed) values of the old variant.
  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
    setSortModel([]);
    setFilterModel({ items: [] });
    setActiveFilter({ items: [] });
    emptyColsVariantRef.current = null;
    setEmptyCols([]);
    setRawRows([]);
    setRowCount(0);
  }, [tableId, selectedVariant]);

  // Debounce the filter model into the applied filter (also reset to page 0)
  useEffect(() => {
    const h = setTimeout(() => {
      setActiveFilter(filterModel);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 400);
    return () => clearTimeout(h);
  }, [filterModel]);

  // Translate the DataGrid models into backend query params
  const buildQuery = useMemo(() => {
    const q = {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    };
    if (sortModel.length > 0) {
      q.sortField = sortModel[0].field;
      q.sortDir = sortModel[0].sort || "asc";
    }
    const item = activeFilter.items?.[0];
    if (item && item.field && item.operator) {
      q.filterField = item.field;
      q.filterOperator = item.operator;
      if (
        item.value !== undefined &&
        item.value !== null &&
        item.value !== ""
      ) {
        q.filterValue = item.value;
      }
    }
    const quick = (activeFilter.quickFilterValues || []).join(" ").trim();
    if (quick) q.quickFilter = quick;
    return q;
  }, [paginationModel, sortModel, activeFilter]);

  // ######### FETCH the current page
  useEffect(() => {
    if (!tableId || !selectedVariant) return;
    let cancelled = false;
    const needEmpties = emptyColsVariantRef.current !== selectedVariant;

    setLoading(true);
    onLoadingChange?.(true);
    setError(null);

    getTableData(tableId, selectedVariant, {
      ...buildQuery,
      withEmptyCols: needEmpties,
    })
      .then((response) => {
        if (cancelled) return;
        setRawRows(response?.data || []);
        setRowCount(response?.total_rows ?? response?.data?.length ?? 0);
        if (needEmpties && Array.isArray(response?.empty_columns)) {
          setEmptyCols(response.empty_columns);
          emptyColsVariantRef.current = selectedVariant;
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setRawRows([]);
        setRowCount(0);
        setError(err.response?.data?.error || t("error"));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onLoadingChange?.(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tableId, selectedVariant, buildQuery, reloadToken]);

  // Load the per-premise setup-key compositions once (for the info popover)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prems = (await getScenarioPremises()) || [];
        if (cancelled) return;
        setPremises(prems);
        // One batch call instead of one request per premise (R45): the backend
        // returns { premise_key: mappings } already in the shape we store.
        const byPremise = await getSetupkeyMappingsBatch(
          prems.map((p) => p.premise_key),
        );
        if (!cancelled) setMappingsByPremise(byPremise || {});
      } catch {
        /* popover just won't show per-premise values */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build DataGrid columns: real columns first, all-empty columns (computed
  // over the whole variant) greyed and pushed to the end; setup-key columns
  // render an info icon instead of the key. Columns sharing a column_group
  // are clustered together (at the first member's position) and get a group
  // header via columnGroupingModel.
  //
  // Derived from columnsMeta (the column schema, keyed by the same column_id
  // that appears in the rows) and NOT from rawRows — so the column defs keep
  // a stable identity while filtering/paging changes the data. Rebuilding
  // columns mid-type would remount the filter panel and drop input focus.
  // Resolve the one translated string the columns use ONCE, so the columns
  // memo does not depend on `t`. `t` from useTranslation(..., { keyPrefix })
  // gets a fresh identity on many renders (inline options object), and having
  // it in the memo deps rebuilt the columns every render → the filter panel
  // remounted and dropped input focus (why typing in the filter value box
  // didn't work here but does on the cockpit table, whose columns don't
  // depend on t). The resolved string is stable, so the memo stays stable.
  const generatedKeyHint = t("generated_key_hint");
  const { columns, columnGroupsMap } = useMemo(() => {
    const ids = Object.keys(columnsMeta || {}).filter(
      (id) => id !== "variant_id",
    );
    if (ids.length === 0) return { columns: [], columnGroupsMap: {} };

    const emptySet = new Set(emptyCols);
    const meta = (id) => columnsMeta?.[id] || {};

    // Cluster grouped columns at the position of their first member; leave
    // ungrouped columns in place. Keeps each group's columns contiguous so
    // the group header spans a single run.
    const clusterByGroup = (list) => {
      const out = [];
      const emitted = new Set();
      list.forEach((id) => {
        if (emitted.has(id)) return;
        const g = meta(id).column_group;
        if (g) {
          list.forEach((id2) => {
            if (!emitted.has(id2) && meta(id2).column_group === g) {
              out.push(id2);
              emitted.add(id2);
            }
          });
        } else {
          out.push(id);
          emitted.add(id);
        }
      });
      return out;
    };

    const nonEmpty = clusterByGroup(ids.filter((id) => !emptySet.has(id)));
    const empty = clusterByGroup(ids.filter((id) => emptySet.has(id)));
    const ordered = [...nonEmpty, ...empty];

    // {field: group} map for the grouped columns panel. The grouping no
    // longer renders as an extra header row on the grid — it lives in the
    // columns selector (GroupedColumnsManagement), where each group gets a
    // show/hide toggle.
    const groupsMap = {};
    ordered.forEach((id) => {
      const g = meta(id).column_group;
      if (g) groupsMap[id] = g;
    });

    const toCol = (id) => {
      const m = meta(id);
      const empt = emptySet.has(id);
      const col = {
        field: id,
        headerName: m.column_name || id,
        flex: 1,
        minWidth: 130,
        filterOperators: TABLE_FILTER_OPERATORS,
        headerClassName: empt ? "rgm-empty-col" : undefined,
        cellClassName: empt ? "rgm-empty-col" : undefined,
      };
      if (m.is_setup_key) {
        col.sortable = false;
        col.filterable = false;
        col.minWidth = 90;
        col.renderCell = (params) => (
          <Tooltip title={generatedKeyHint}>
            <IconButton
              size="small"
              className={classes.keyInfoBtn}
              style={{ color: "#2b2f33" }}
              onClick={(e) =>
                setKeyPopover({
                  anchor: e.currentTarget,
                  columnId: id,
                  columnName: m.column_name || id,
                  row: params.row,
                })
              }
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
      return col;
    };

    return {
      columns: ordered.map(toCol),
      columnGroupsMap: groupsMap,
    };
  }, [columnsMeta, emptyCols, classes.keyInfoBtn, generatedKeyHint]);

  // Rows depend only on the current page of data; kept separate from columns
  // so data changes don't rebuild (and remount) the column defs.
  const rows = useMemo(
    () =>
      rawRows.map((r, i) => ({
        id: paginationModel.page * paginationModel.pageSize + i,
        ...r,
      })),
    [rawRows, paginationModel],
  );

  const handleDownloadProcessed = async () => {
    setIsDownloading(true);
    const toastId = toast.loading(t("download_in_progress"));
    try {
      await downloadProcessedVariant(tableId, selectedVariant);
      toast.update(toastId, {
        render: t("download_ready"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (err) {
      toast.update(toastId, {
        render: err.response?.data?.error || t("download_error"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (error) {
    return (
      <Typography className={classes.errorMessage}>
        {t("error")}: {error}
      </Typography>
    );
  }
  if (!selectedVariant) {
    return (
      <Typography className={classes.noInputMessage}>
        {t("no_data_available")}
      </Typography>
    );
  }

  return (
    <Box className={classes.card}>
      <Box className={classes.gridWrap}>
        <DataGrid
          // Remount on table/variant change so the (now uncontrolled) filter,
          // sort and page reset cleanly to a fresh grid for the new variant.
          // The key only changes on switch — never mid-interaction — so typing
          // in the filter never remounts.
          key={`${tableId}:${selectedVariant}`}
          className={classes.grid}
          rows={rows}
          columns={columns}
          density="compact"
          loading={loading}
          disableRowSelectionOnClick
          showToolbar
          // server-side data operations
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          // Filter is left UNCONTROLLED (no filterModel prop) so the grid owns
          // the filter-panel input — a controlled filterModel re-rendered the
          // value box on every keystroke and dropped focus (couldn't type).
          // We still capture changes here to drive the server-side query; the
          // key above resets the panel on variant switch. Mirrors the cockpit
          // table, which is uncontrolled and where typing already works.
          onFilterModelChange={setFilterModel}
          slots={{
            toolbar: PreviewToolbar,
            // grouped columns selector (replaces the on-grid group header row)
            columnsManagement: GroupedColumnsManagement,
          }}
          slotProps={{
            // Skeleton overlay while loading — including the variant switch,
            // not only the first load — instead of dimming the previous rows.
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            columnsManagement: {
              groups: columnGroupsMap,
              searchPlaceholder: t("search"),
              clientColor,
            },
            filterPanel: TABLE_FILTER_PANEL_PROPS,
            toolbar: {
              countText: t("showing_rows", {
                showing: showing.toLocaleString(),
                total: rowCount.toLocaleString(),
              }),
              onExport: handleDownloadProcessed,
              exportDisabled: !selectedVariant || isDownloading,
              isDownloading,
              exportLabel: t("download_processed"),
              countClass: classes.rowCount,
              searchLabel: t("search"),
              spinnerColor: clientColor,
            },
          }}
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
        />
      </Box>

      <Popover
        open={Boolean(keyPopover)}
        anchorEl={keyPopover?.anchor}
        onClose={() => setKeyPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        classes={{ paper: classes.pop }}
      >
        {keyPopover && (
          <Box>
            <Typography className={classes.popTitle}>
              {t("generated_key_title")}
            </Typography>
            <Typography className={classes.popDesc}>
              {t("generated_key_desc")}
            </Typography>
            <Typography className={classes.popMeta}>
              {t("column")}:{" "}
              <span className={classes.popMetaB}>{keyPopover.columnName}</span>
            </Typography>
            {premises.map((p) => {
              const comp =
                mappingsByPremise[p.premise_key]?.[tableId]?.[
                  keyPopover.columnId
                ];
              return (
                <Box key={p.premise_key} className={classes.popPrem}>
                  <span className={classes.popPremLbl}>
                    {p.premise_label || p.premise_key}
                  </span>
                  <br />
                  <span className={classes.popPremVal}>
                    {composeKey(comp, keyPopover.row)}
                  </span>
                </Box>
              );
            })}
          </Box>
        )}
      </Popover>
    </Box>
  );
}

TabTablePreview.propTypes = {
  tableId: PropTypes.string,
  columnsMeta: PropTypes.object,
  selectedVariant: PropTypes.number,
  reloadToken: PropTypes.number,
  onLoadingChange: PropTypes.func,
};
